// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useMemo, useRef } from 'react'
import clsx from 'clsx'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import styles from './VersusYours.module.css'
import LabSpark from './LabSpark'
import type { LabChartPaths, LabTabKey } from 'lib/dealhunt/audioChart'
import { useDeclaredContext } from 'lib/owner-notes/declared'
import { THIS_COLOR, YOURS_COLOR, useCurves, useInView } from 'lib/owner-notes/curves'
import { useOwnerTrack } from 'lib/owner-notes/analytics'
import { useLabCompare, type LabTarget } from 'lib/owner-notes/lab-compare'
import { useAllDetailsOpen } from 'lib/owner-notes/details-open'
import {
  compareRows,
  sameProduct,
  scaleRowText,
  sentenceCase,
  type CompareRow,
  type DeltaNumbers,
  type ScaleRow,
} from 'lib/owner-notes/deltas'

interface VersusYoursProps {
  /** v3 card node id (= objectID) + slug, for the "this is yours" check. */
  nodeId: string
  slug: string | null
  /** "{brand} {name}" as the card prints it — the GA4 target and the chart labels. */
  productName: string
  /** The card's PDP href; the lab dialog's "Open the full lab data" link deep-links into its lab
   *  section. */
  pdpHref: string | null
  numbers: DeltaNumbers
  /**
   * `full` (the default, the category grid): the block carries its own "Versus yours" heading
   * and colour key. `bare`: the caller already named the pair and drew the key above it — the
   * guide's pick block does — so printing both would be two headings and two keys for one
   * comparison. The charts, rows and details are identical either way.
   */
  chrome?: 'full' | 'bare'
  /** Where this block sits, on the `lab_open` event only. Omitted on the category page, whose
   *  events must stay byte-identical; the guide's pick block sends `pick`. */
  location?: string
  /**
   * False: no sparklines, no "Loading lab curves…" — the rows, the details and "Same as yours"
   * only. For a caller that draws the charts itself at full size (the guide's pick block mounts
   * `LabChartPanel` above this), where a second pair of 150px sparks would be the same two
   * curves twice. Default true: the category grid is unchanged.
   */
  charts?: boolean
}

/** Two dots on the axis's own scale, the gap between them tinted by direction. */
const ScaleRail: React.VFC<{ row: ScaleRow }> = ({ row }) => {
  const pct = (v: number): string => `${(((v - row.min) / (row.max - row.min)) * 100).toFixed(1)}%`
  const left = Math.min(row.mine, row.theirs)
  const width = `${((Math.abs(row.mine - row.theirs) / (row.max - row.min)) * 100).toFixed(1)}%`
  return (
    <span className={styles.rail} aria-hidden="true">
      <span
        className={clsx(styles.gap, styles[`gap_${row.direction}`])}
        style={{ left: pct(left), width }}
      />
      <span
        className={styles.dot}
        style={{ left: pct(row.mine), backgroundColor: YOURS_COLOR }}
        title={`Yours: ${row.mine.toFixed(1)}`}
      />
      <span
        className={styles.dot}
        style={{ left: pct(row.theirs), backgroundColor: THIS_COLOR }}
        title={`This card: ${row.theirs.toFixed(1)}`}
      />
    </span>
  )
}

/** Which attenuation measure both pairs carry — ANC+isolation first, else passive isolation. */
function attenuation(
  mine: LabChartPaths,
  theirs: LabChartPaths
): { label: string; mine: LabChartPaths['anc']; theirs: LabChartPaths['anc'] } | null {
  if (mine.anc && theirs.anc) {
    return { label: 'ANC', mine: mine.anc, theirs: theirs.anc }
  }
  if (mine.isolation && theirs.isolation) {
    return { label: 'Isolation', mine: mine.isolation, theirs: theirs.isolation }
  }
  return null
}

/** This card's attenuation measure on its own — ANC first, else passive isolation. */
function soloAttenuation(
  theirs: LabChartPaths
): { label: string; curve: NonNullable<LabChartPaths['anc']> } | null {
  if (theirs.anc) {
    return { label: 'ANC', curve: theirs.anc }
  }
  if (theirs.isolation) {
    return { label: 'Isolation', curve: theirs.isolation }
  }
  return null
}

/** One comparison row: a scale rail (numbers on phones), an absent note, a text pair or owners. */
const RowItem: React.VFC<{ row: CompareRow; wide: boolean }> = ({ row, wide }) => (
  <li className={styles.row}>
    <span className={styles.k}>{sentenceCase(row.label)}</span>
    {row.kind === 'scale' &&
      (wide ? (
        <span className={styles.scale}>
          <span className={styles.n} style={{ color: YOURS_COLOR }}>
            {row.mine.toFixed(1)}
          </span>
          <ScaleRail row={row} />
          <span className={styles.n} style={{ color: THIS_COLOR }}>
            {row.theirs.toFixed(1)}
          </span>
          <span className={styles.srOnly}>{scaleRowText(row)}</span>
        </span>
      ) : (
        <span className={clsx(styles.v, styles[`v_${row.direction}`])}>
          {row.theirs.toFixed(1)}{' '}
          {/* No "· +0.4 /10" tail: the arithmetic is the two numbers either side of "vs", and
              the colour already says which way it went. Spelling it out cost a second line in a
              157px column — with "/10" orphaned on it. The full sentence stays for screen
              readers, which get no colour. */}
          <span className={styles.dim}>{`vs ${row.mine.toFixed(1)}`}</span>
          <span className={styles.srOnly}>{scaleRowText(row)}</span>
        </span>
      ))}
    {row.kind === 'absent' && (
      <>
        <span className={clsx(styles.v, styles.dim)}>{row.text}</span>
        {row.note && <span className={styles.note}>{row.note}</span>}
      </>
    )}
    {row.kind === 'text' && (
      <span className={clsx(styles.v, styles[`v_${row.direction}`])}>
        {row.theirs}{' '}
        {/* A list row's note already states the difference ("yours adds LDAC"); repeating
            the whole list after "vs your" would double the row. */}
        <span className={styles.dim}>{row.note ?? `vs your ${row.mine}`}</span>
      </span>
    )}
  </li>
)

/**
 * "Versus yours" on a category card (contract #63, ticket
 * dealhunt/docs/sg-owner-notes-versus-yours-ticket.md): after hydration inside a page that
 * owns a declaration — lab sparklines where both pairs were measured, paired scores on their
 * own scale, battery, the differing specs. Owner counts are not here: they are the card's
 * own Owners block (`CardOwners`, #115).
 * Both values always; price and SG score never (the card already carries them). No sort.
 *
 * Ticket "Versus yours without lab data" (2026-09-09): the border is for charts, the colour
 * key for paired values — a specs-tier card is an unboxed list; the differing specs are
 * and when only THIS card was lab-tested its curves draw alone in blue, so a tested
 * card keeps its charts whatever the reader owns. The "not lab-tested" disclosure is said
 * once, on the toolbar chip and the own tile, never here.
 */
const VersusYours: React.VFC<VersusYoursProps> = ({
  nodeId,
  slug,
  productName,
  pdpHref,
  numbers,
  chrome = 'full',
  location,
  charts = true,
}) => {
  const declared = useDeclaredContext()?.declared ?? null
  const yours = declared
    ? sameProduct({ nodeId: declared.nodeId, slug: declared.slug }, { nodeId, slug })
    : false
  // `numbers` is stable per card render (the card is memoized on its model), so the rows
  // are rebuilt only for a new pair, not on every context tick.
  const comparison = useMemo(
    () => (declared && !yours ? compareRows(declared, numbers) : null),
    [declared, yours, numbers]
  )
  const tier = comparison?.tier ?? null

  const ref = useRef<HTMLDivElement>(null)
  const lab = tier === 'lab'
  const theirId = numbers.clearbuyProductId ?? null
  // Curves are wanted whenever THIS card has a lab id (paired on the lab tier, alone
  // otherwise); the root div only exists once something renders, so the observer arms then.
  const wantCurves = Boolean(comparison) && theirId !== null
  const inView = useInView(ref, wantCurves)
  const myCurves = useCurves(declared?.clearbuyProductId ?? null, lab && inView)
  const theirCurves = useCurves(theirId, wantCurves && inView)
  const responsive = ResponsiveContext.useOptionalContainer()
  const wide = responsive?.ready === true && responsive.md
  const trackOwner = useOwnerTrack({ nodeId, name: productName })
  // The lab dialog and the pinned drawer (ticket "Lab Charts Off the Card", 2026-09-08):
  // present only inside the category page's provider.
  const labCompare = useLabCompare()
  // "All details" — isolation, the differing specs, the identical ones. Closed by default: at
  // a glance the block is the sparklines, Sound, ANC, battery; four cards of nine rows each
  // was a wall (Guillaume, 2026-09-05). One switch for every card on the page (2026-09-09).
  const [open, setOpen] = useAllDetailsOpen()

  const rows: CompareRow[] = comparison?.rows ?? []
  const details: CompareRow[] = comparison?.details ?? []
  const atten = myCurves && theirCurves ? attenuation(myCurves, theirCurves) : null
  const freq =
    myCurves?.frequency && theirCurves?.frequency
      ? { mine: myCurves.frequency, theirs: theirCurves.frequency }
      : null
  const curvesLoading =
    inView && ((lab && myCurves === undefined) || (wantCurves && theirCurves === undefined))
  const paired = Boolean(freq || atten)
  // This card's own curves, alone: the declared product has none (never lab-tested, or a
  // lab id whose measurement is empty) and this card's have arrived. Never while the
  // declared curves are still loading — a pair must not flash as a solo first.
  const soloOk = !paired && !curvesLoading && (!lab || myCurves !== undefined)
  const soloFreq = soloOk && theirCurves?.frequency ? theirCurves.frequency : null
  const soloAtten = soloOk && theirCurves ? soloAttenuation(theirCurves) : null
  // `charts: false` leaves the curves fetched (the cache is shared with whoever IS drawing
  // them) but draws none here, and with them goes the box: the border is for charts.
  const hasSparks = charts && (paired || soloFreq !== null || soloAtten !== null)
  const showLoading = charts && curvesLoading
  // Differing specs alone are a block too (2026-09-09): a card with no battery figure and no
  // lab id still differs from yours on its spec sheet.
  const showBlock = Boolean(
    comparison &&
      (rows.length > 0 ||
        details.length > 0 ||
        hasSparks ||
        showLoading ||
        comparison.same.length > 0)
  )
  // Nothing differs and nothing is charted, only identical specs (2026-09-11, #115): the
  // block says so in one line, not behind "All details" — the owners row used to hold such a
  // block open, and Guillaume kept the line.
  const matchesOnly = rows.length === 0 && details.length === 0 && !hasSparks && !showLoading
  // The colour key reads against paired values: rail dots and paired numbers (scored),
  // both curves (lab). A specs-tier card has none — one blue number keys nothing.
  const legend = tier !== 'specs' && chrome === 'full'

  if (!yours && !showBlock) {
    return null
  }

  const labTarget: LabTarget = {
    nodeId,
    slug,
    name: productName,
    pdpHref,
    clearbuyProductId: theirId,
  }
  const openLab = labCompare
    ? (tab: Exclude<LabTabKey, 'mdaqs'>): void => {
        // No `location` on the category page: its events stay exactly as they were.
        trackOwner('lab_open', location ? { lab_tab: tab, location } : { lab_tab: tab })
        labCompare.openDialog(labTarget, tab)
      }
    : null

  return (
    <div className={styles.root} ref={ref}>
      {yours && <span className={styles.yours}>{'Yours'}</span>}
      {showBlock && declared && comparison && (
        <section
          // The border is for charts: a one-row list reads as a fact, a one-row box as an
          // empty container.
          className={clsx(styles.block, { [styles.boxed]: hasSparks || showLoading })}
          aria-label={`${productName} versus your ${declared.name}`}
        >
          {chrome === 'full' && (
            <header className={styles.head}>
              <span className={styles.heading}>{'Versus yours'}</span>
            </header>
          )}
          {legend && (
            /* The colour key (cards are read one at a time): orange is the declared pair,
               blue this card — the sparklines, the rail dots and the paired numbers follow it. */
            <span className={styles.legend} aria-hidden="true">
              <span className={styles.key}>
                <span className={styles.swatch} style={{ backgroundColor: YOURS_COLOR }} />
                <span className={styles.keyName} title={declared.name}>
                  {declared.name}
                </span>
                <span className={styles.keyRole}>{'· yours'}</span>
              </span>
              <span className={styles.key}>
                <span className={styles.swatch} style={{ backgroundColor: THIS_COLOR }} />
                <span className={styles.keyRole}>{'this card'}</span>
              </span>
            </span>
          )}

          {charts && paired && (
            <div className={styles.sparks}>
              {freq && (
                <LabSpark
                  label="Frequency"
                  kind="frequency"
                  mine={freq.mine.points}
                  theirs={freq.theirs.points}
                  productName={productName}
                  declaredName={declared.name}
                  onOpen={openLab ? () => openLab('freq') : undefined}
                />
              )}
              {atten && atten.mine && atten.theirs && (
                <LabSpark
                  label={atten.label}
                  kind="attenuation"
                  mine={atten.mine.points}
                  theirs={atten.theirs.points}
                  productName={productName}
                  declaredName={declared.name}
                  onOpen={openLab ? () => openLab('atten') : undefined}
                />
              )}
            </div>
          )}
          {charts && (soloFreq || soloAtten) && (
            <div className={styles.sparks}>
              {soloFreq && (
                <LabSpark
                  label="Frequency"
                  kind="frequency"
                  mine={null}
                  theirs={soloFreq.points}
                  productName={productName}
                  declaredName={declared.name}
                  onOpen={openLab ? () => openLab('freq') : undefined}
                />
              )}
              {soloAtten && (
                <LabSpark
                  label={soloAtten.label}
                  kind="attenuation"
                  mine={null}
                  theirs={soloAtten.curve.points}
                  productName={productName}
                  declaredName={declared.name}
                  onOpen={openLab ? () => openLab('atten') : undefined}
                />
              )}
            </div>
          )}
          {showLoading && (
            <p className={styles.loading} aria-live="polite">
              {'Loading lab curves…'}
            </p>
          )}

          {rows.length > 0 && (
            <ul className={styles.rows}>
              {rows.map(row => (
                <RowItem key={row.key} row={row} wide={wide} />
              ))}
            </ul>
          )}
          {matchesOnly && (
            <p className={styles.same}>{`Same as yours: ${comparison.same.join(', ')}`}</p>
          )}
          {!matchesOnly && (details.length > 0 || comparison.same.length > 0) && (
            <div className={styles.details}>
              {details.length === 0 && (
                <p className={styles.also}>
                  <span className={styles.alsoLabel}>{'Everything else matches yours.'}</span>
                </p>
              )}
              {/* One control, not a row of chips (2026-09-12). Each chip was a second way to
                  open one row, on top of this switch — two disclosure models for the same rows,
                  and the pills read as filters. The count is what the chips were really for:
                  it says how much is behind the switch without spending five lines naming it. */}
              {details.length > 0 && (
                <button
                  type="button"
                  className={styles.detailsToggle}
                  aria-expanded={open}
                  onClick={() => setOpen(!open)}
                >
                  <span>{open ? 'Hide details' : `All details (${details.length})`}</span>
                  <span className={styles.detailsChevron} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="m4 6.5 4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              )}
              {open && (
                <>
                  {details.length > 0 && (
                    <ul className={styles.rows}>
                      {details.map(row => (
                        <RowItem key={row.key} row={row} wide={wide} />
                      ))}
                    </ul>
                  )}
                  {comparison.same.length > 0 && (
                    <p className={styles.same}>{`Same as yours: ${comparison.same.join(', ')}`}</p>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  )
}

export default VersusYours
